/**
 * Content type definitions and utilities
 */

export enum ContentType {
  SOCIAL_POST = 'Social Post',
  INSTAGRAM_STORY = 'Instagram Story',
  FACEBOOK_STORY = 'Facebook Story',
  INSTAGRAM_REEL = 'Instagram Reel',
  TIKTOK_VIDEO = 'TikTok Video',
  CAROUSEL = 'Carousel',
}

export type ContentTypeKey = keyof typeof ContentType;
export type ContentTypeValue = `${ContentType}`;

// Platform compatibility matrix
export const PLATFORM_CONTENT_SUPPORT: Record<ContentType, string[]> = {
  [ContentType.SOCIAL_POST]: ['instagram', 'facebook', 'twitter', 'linkedin', 'tiktok'],
  [ContentType.INSTAGRAM_STORY]: ['instagram'],
  [ContentType.FACEBOOK_STORY]: ['facebook'],
  [ContentType.INSTAGRAM_REEL]: ['instagram'],
  [ContentType.TIKTOK_VIDEO]: ['tiktok'],
  [ContentType.CAROUSEL]: ['instagram', 'facebook', 'linkedin'],
};

// Content type metadata
export interface ContentTypeMetadata {
  name: string;
  description: string;
  icon: string;
  aspectRatio?: {
    width: number;
    height: number;
  };
  maxDuration?: number;
  minDuration?: number;
  supportedMedia: ('image' | 'video')[];
  platforms: string[];
}

export const CONTENT_TYPE_METADATA: Record<ContentType, ContentTypeMetadata> = {
  [ContentType.SOCIAL_POST]: {
    name: 'Social Post',
    description: 'Standard social media post for feeds',
    icon: 'FileTextIcon',
    supportedMedia: ['image', 'video'],
    platforms: ['instagram', 'facebook', 'twitter', 'linkedin', 'tiktok'],
  },
  [ContentType.INSTAGRAM_STORY]: {
    name: 'Instagram Story',
    description: 'Vertical story format for Instagram',
    icon: 'Image',
    aspectRatio: { width: 9, height: 16 },
    maxDuration: 15,
    supportedMedia: ['image', 'video'],
    platforms: ['instagram'],
  },
  [ContentType.FACEBOOK_STORY]: {
    name: 'Facebook Story',
    description: 'Vertical story format for Facebook',
    icon: 'Image',
    aspectRatio: { width: 9, height: 16 },
    maxDuration: 20,
    supportedMedia: ['image', 'video'],
    platforms: ['facebook'],
  },
  [ContentType.INSTAGRAM_REEL]: {
    name: 'Instagram Reel',
    description: 'Short-form vertical video for Instagram',
    icon: 'Play',
    aspectRatio: { width: 9, height: 16 },
    maxDuration: 90,
    minDuration: 15,
    supportedMedia: ['video'],
    platforms: ['instagram'],
  },
  [ContentType.TIKTOK_VIDEO]: {
    name: 'TikTok Video',
    description: 'Short-form vertical video for TikTok',
    icon: 'Play',
    aspectRatio: { width: 9, height: 16 },
    maxDuration: 60,
    minDuration: 15,
    supportedMedia: ['video'],
    platforms: ['tiktok'],
  },
  [ContentType.CAROUSEL]: {
    name: 'Carousel',
    description: 'Multi-image swipeable post',
    icon: 'GridNine',
    supportedMedia: ['image'],
    platforms: ['instagram', 'facebook', 'linkedin'],
  },
};

// Utility functions
export const isStoryType = (contentType: ContentType): boolean => {
  return contentType === ContentType.INSTAGRAM_STORY || contentType === ContentType.FACEBOOK_STORY;
};

export const isReelType = (contentType: ContentType): boolean => {
  return contentType === ContentType.INSTAGRAM_REEL || contentType === ContentType.TIKTOK_VIDEO;
};

export const isVerticalContent = (contentType: ContentType): boolean => {
  return isStoryType(contentType) || isReelType(contentType);
};

export const supportsVideo = (contentType: ContentType): boolean => {
  const metadata = CONTENT_TYPE_METADATA[contentType];
  return metadata.supportedMedia.includes('video');
};

export const supportsImage = (contentType: ContentType): boolean => {
  const metadata = CONTENT_TYPE_METADATA[contentType];
  return metadata.supportedMedia.includes('image');
};

export const getContentTypesForPlatform = (platform: string): ContentType[] => {
  return Object.entries(PLATFORM_CONTENT_SUPPORT)
    .filter(([, platforms]) => platforms.includes(platform))
    .map(([contentType]) => contentType as ContentType);
};

export const getSupportedPlatforms = (contentType: ContentType): string[] => {
  return PLATFORM_CONTENT_SUPPORT[contentType] || [];
};

// TextT guards
export const isValidContentType = (value: string): value is ContentType => {
  return Object.values(ContentType).includes(value as ContentType);
};

// Default content type for new posts
export const DEFAULT_CONTENT_TYPE = ContentType.SOCIAL_POST;

// Legacy string-based content type conversion (for migration)
export const convertLegacyContentType = (legacyType: string): ContentType => {
  const mapping: Record<string, ContentType> = {
    'Social Post': ContentType.SOCIAL_POST,
    'Instagram Story': ContentType.INSTAGRAM_STORY,
    'Facebook Story': ContentType.FACEBOOK_STORY,
    'Instagram Reel': ContentType.INSTAGRAM_REEL,
    'TikTok Video': ContentType.TIKTOK_VIDEO,
    Carousel: ContentType.CAROUSEL,
    // Handle variations
    Post: ContentType.SOCIAL_POST,
    Story: ContentType.INSTAGRAM_STORY,
    Reel: ContentType.INSTAGRAM_REEL,
    TikTok: ContentType.TIKTOK_VIDEO,
  };

  return mapping[legacyType] || ContentType.SOCIAL_POST;
};
